import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtNum } from "@/lib/api";
import { PageHeader, Card, Field, TextInput, NumberInput, SelectInput, StatusBadge, CrudActions } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LabourAgreements from "@/pages/LabourAgreements";

const CHARGE_TREAT = ["Recoverable", "Pass-through", "SRM Expense", "Other"];
const TAX_TREAT = ["Non-GST", "Exempt", "Taxable", "Other"];

const ENTITIES = {
  warehouses: {
    label: "Warehouses",
    cols: [["code", "Code"], ["name", "Name"], ["location", "Location"]],
    fields: [["code", "Code", "text", true], ["name", "Name", "text", true], ["location", "Location", "text"]],
  },
  stacks: {
    label: "Stacks",
    cols: [
      ["code", "Code"],
      ["warehouse_name", "Warehouse"],
      ["capacity_config_name", "Config"],
      ["occupancy", "Space used", (r) => `${r.occupancy?.space_used_pct ?? r.occupancy?.utilisation ?? 0}%`],
      ["stack_complete", "Status", (r) => (r.stack_complete || r.occupancy?.stack_complete) ? "Complete" : "Open"],
    ],
    fields: [
      ["code", "Code", "text", true],
      ["warehouse_id", "Warehouse", "fk:warehouses", true],
      ["capacity_config_id", "Capacity Config", "fk:capacity_configs", true],
      ["tolerance_pct", "Tolerance ±%", "number"],
    ],
  },
  capacity_configs: {
    label: "Stack Capacity",
    cols: [
      ["name", "Name"],
      ["area_sqft", "Area (sqft)", (r) => fmtNum(r.area_sqft)],
      ["bag_limits", "Bag limits", (r) => (r.bag_limits || []).map((b) => `${b.bag_type_name || "Bag"}: ${fmtNum(b.max_bags)}`).join(" · ") || (r.max_bags ? fmtNum(r.max_bags) : "—")],
    ],
    fields: [
      ["name", "Name", "text", true],
      ["area_sqft", "Area (sqft)", "number"],
      ["length_ft", "Length (ft)", "number"],
      ["width_ft", "Width (ft)", "number"],
    ],
    hasBagLimits: true,
  },
  stock_items: {
    label: "Stock Items",
    cols: [["code", "Code"], ["name", "Name"], ["commodity", "Commodity"], ["variety", "Variety"], ["max_shrinkage_pct", "Max Shrinkage %", (r) => `${r.max_shrinkage_pct}%`]],
    fields: [["code", "Code", "text", true], ["name", "Name", "text", true], ["commodity", "Commodity", "text"], ["variety", "Variety", "text"], ["grade", "Grade", "text"], ["max_shrinkage_pct", "Max Shrinkage %", "number"]],
  },
  bag_types: {
    label: "Bag Types",
    cols: [["name", "Name"], ["nominal_capacity", "Capacity"], ["material", "Material"], ["charge_rate", "Rate", (r) => `₹${r.charge_rate}`]],
    fields: [["name", "Name", "text", true], ["nominal_capacity", "Nominal Capacity", "text"], ["material", "Material", "text"], ["charge_rate", "Charge Rate", "number"]],
  },
  vehicles: {
    label: "Vehicles",
    cols: [["vehicle_number", "Number"], ["vehicle_type", "Type"], ["driver", "Driver"], ["driver_mobile", "Mobile"]],
    fields: [["vehicle_number", "Vehicle Number", "text", true], ["vehicle_type", "Type", "text"], ["owner_party_id", "Owner", "fk:parties"], ["transporter_party_id", "Transporter", "fk:parties"], ["driver", "Driver", "text"], ["driver_mobile", "Driver Mobile", "text"]],
  },
  charge_types: {
    label: "Charge Types",
    cols: [["name", "Name"], ["charge_treatment", "Treatment"], ["tax_treatment", "Tax"], ["default_rate", "Rate", (r) => `₹${r.default_rate}`]],
    fields: [["name", "Name", "text", true], ["charge_treatment", "Charge Treatment", "select:CHARGE"], ["tax_treatment", "Tax Treatment", "select:TAX"], ["default_rate", "Default Rate", "number"]],
  },
};

function useEntityList(entity) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["masters", entity], queryFn: async () => (await api.get(`/masters/${entity}`)).data });
  return { ...query, invalidate: () => qc.invalidateQueries({ queryKey: ["masters", entity] }) };
}

export default function Masters() {
  const { canManageUsers } = useAuth();
  return (
    <div data-testid="masters-page">
      <PageHeader title="Masters" subtitle="Create, edit and deactivate warehouses, stacks, items, bags, vehicles, charges and users" />
      <Tabs defaultValue="warehouses">
        <TabsList className="mb-4 flex-wrap h-auto">
          {Object.entries(ENTITIES).map(([k, v]) => (
            <TabsTrigger key={k} value={k} data-testid={`mtab-${k}`}>{v.label}</TabsTrigger>
          ))}
          <TabsTrigger value="labour_agreements" data-testid="mtab-labour_agreements">Labour Rates</TabsTrigger>
          {canManageUsers && <TabsTrigger value="users" data-testid="mtab-users">Users</TabsTrigger>}
        </TabsList>
        {Object.keys(ENTITIES).map((k) => (
          <TabsContent key={k} value={k}><EntityPanel entity={k} /></TabsContent>
        ))}
        <TabsContent value="labour_agreements"><LabourAgreements /></TabsContent>
        {canManageUsers && <TabsContent value="users"><UsersPanel /></TabsContent>}
      </Tabs>
    </div>
  );
}

function EntityPanel({ entity }) {
  const cfg = ENTITIES[entity];
  const { data = [], isLoading, invalidate } = useEntityList(entity);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const remove = async (row) => {
    if (!window.confirm(`Delete / deactivate ${cfg.label.toLowerCase()} "${row.name || row.code || row.vehicle_number || row.id}"?`)) return;
    try {
      const { data } = await api.delete(`/masters/${entity}/${row.id}`);
      toast.success(data.status === "deactivated" ? "Deactivated" : "Deleted");
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
  };

  const restore = async (row) => {
    try {
      await api.put(`/masters/${entity}/${row.id}`, { active: true });
      toast.success("Restored");
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid={`add-${entity}-btn`} onClick={() => { setEditing(null); setOpen(true); }}
          className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900 transition-colors">
          <Plus size={16} /> Add {cfg.label}
        </button>
      </div>
      {isLoading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
                <tr>{cfg.cols.map((c) => <th key={c[0]} className="px-4 py-3">{c[1]}</th>)}<th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {data.length === 0 && <tr><td colSpan={cfg.cols.length + 2} className="px-4 py-8 text-center text-stone-400">No records</td></tr>}
                {data.map((r) => (
                  <tr key={r.id} className="border-t border-stone-100 even:bg-stone-50/50">
                    {cfg.cols.map((c) => <td key={c[0]} className="px-4 py-3">{c[2] ? c[2](r) : (r[c[0]] ?? "—")}</td>)}
                    <td className="px-4 py-3"><StatusBadge tone={r.active === false ? "neutral" : "emerald"}>{r.active === false ? "Inactive" : "Active"}</StatusBadge></td>
                    <td className="px-4 py-3">
                      <CrudActions
                        testid={`${entity}-${r.id}`}
                        inactive={r.active === false}
                        onEdit={() => { setEditing(r); setOpen(true); }}
                        onDelete={() => remove(r)}
                        onRestore={r.active === false ? () => restore(r) : undefined}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <EntityDialog open={open} onClose={() => { setOpen(false); setEditing(null); }} entity={entity} cfg={cfg} record={editing} onSaved={invalidate} />
    </div>
  );
}

function EntityDialog({ open, onClose, entity, cfg, record, onSaved }) {
  const [f, setF] = useState({});
  const [bagLimits, setBagLimits] = useState([{ bag_type_id: "", max_bags: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (record) {
      const next = {};
      cfg.fields.forEach(([k]) => { next[k] = record[k] ?? ""; });
      setF(next);
      if (cfg.hasBagLimits) {
        const limits = record.bag_limits?.length
          ? record.bag_limits.map((b) => ({ bag_type_id: String(b.bag_type_id || ""), max_bags: String(b.max_bags || "") }))
          : [{ bag_type_id: "", max_bags: "" }];
        setBagLimits(limits);
      }
    } else {
      setF(entity === "stacks" ? { tolerance_pct: "10" } : {});
      setBagLimits([{ bag_type_id: "", max_bags: "" }]);
    }
  }, [open, record, entity, cfg]);

  const parties = useQuery({ queryKey: ["masters", "parties"], queryFn: async () => (await api.get("/masters/parties")).data, enabled: open }).data || [];
  const warehouses = useQuery({ queryKey: ["masters", "warehouses"], queryFn: async () => (await api.get("/masters/warehouses")).data, enabled: open }).data || [];
  const configs = useQuery({ queryKey: ["masters", "capacity_configs"], queryFn: async () => (await api.get("/masters/capacity_configs")).data, enabled: open }).data || [];
  const bagTypes = useQuery({ queryKey: ["masters", "bag_types"], queryFn: async () => (await api.get("/masters/bag_types")).data, enabled: open && cfg.hasBagLimits }).data || [];

  const fkOptions = (kind) => {
    if (kind === "parties") return parties.map((p) => ({ value: p.id, label: `${p.name} (${p.code})` }));
    if (kind === "warehouses") return warehouses.map((w) => ({ value: w.id, label: w.name }));
    if (kind === "capacity_configs") {
      return configs.map((c) => {
        const lim = (c.bag_limits || []).map((b) => `${b.bag_type_name || "Bag"} ${fmtNum(b.max_bags)}`).join(", ");
        return { value: c.id, label: lim ? `${c.name} (${lim})` : `${c.name}${c.area_sqft ? ` · ${fmtNum(c.area_sqft)} sqft` : ""}` };
      });
    }
    return [];
  };

  const save = async () => {
    for (const [k, , , req] of cfg.fields) if (req && !f[k]) return toast.error("Please fill all required fields");
    if (cfg.hasBagLimits) {
      const valid = bagLimits.filter((r) => r.bag_type_id && Number(r.max_bags) > 0);
      if (!valid.length) return toast.error("Add at least one bag type with max bags");
    }
    setSaving(true);
    const body = { ...f };
    cfg.fields.forEach(([k, , type]) => {
      if (type === "number" && body[k] !== undefined && body[k] !== "") body[k] = Number(body[k]);
      if (type?.startsWith("fk:")) body[k] = body[k] ? Number(body[k]) : null;
    });
    if (cfg.hasBagLimits) {
      body.bag_limits = bagLimits
        .filter((r) => r.bag_type_id && Number(r.max_bags) > 0)
        .map((r) => ({ bag_type_id: Number(r.bag_type_id), max_bags: Number(r.max_bags) }));
    }
    try {
      if (record) {
        await api.put(`/masters/${entity}/${record.id}`, body);
        toast.success(`${cfg.label} updated`);
      } else {
        await api.post(`/masters/${entity}`, body);
        toast.success(`${cfg.label} added`);
      }
      onSaved(); setF({}); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${record ? "edit" : "add"}-${entity}-dialog`}>
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit" : "Add"} {cfg.label}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {cfg.fields.map(([k, label, type, req]) => (
            <Field key={k} label={label} required={req}>
              {type === "text" && <TextInput value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`field-${k}`} />}
              {type === "number" && <NumberInput value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`field-${k}`} />}
              {type === "select:CHARGE" && <SelectInput options={CHARGE_TREAT.map((x) => ({ value: x, label: x }))} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}
              {type === "select:TAX" && <SelectInput options={TAX_TREAT.map((x) => ({ value: x, label: x }))} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}
              {type?.startsWith("fk:") && <SelectInput options={fkOptions(type.slice(3))} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`field-${k}`} />}
            </Field>
          ))}
          {cfg.hasBagLimits && (
            <div className="col-span-2 space-y-2 rounded-lg border border-stone-200 p-3">
              <div className="text-xs font-bold uppercase tracking-wide text-stone-500">Max bags per bag type (mixed storage)</div>
              <p className="text-xs text-stone-500">Example: 600 sqft → 3000×50kg and 2000×100kg. Space used = sum of (bags ÷ max for type).</p>
              {bagLimits.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <Field label="Bag type">
                    <SelectInput
                      options={bagTypes.filter((b) => b.active !== false).map((b) => ({ value: b.id, label: b.name }))}
                      value={row.bag_type_id}
                      onChange={(e) => setBagLimits(bagLimits.map((r, j) => j === i ? { ...r, bag_type_id: e.target.value } : r))}
                    />
                  </Field>
                  <Field label="Max bags">
                    <NumberInput value={row.max_bags} onChange={(e) => setBagLimits(bagLimits.map((r, j) => j === i ? { ...r, max_bags: e.target.value } : r))} />
                  </Field>
                  <button type="button" onClick={() => setBagLimits(bagLimits.filter((_, j) => j !== i))} className="h-10 px-2 text-xs text-red-600">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setBagLimits([...bagLimits, { bag_type_id: "", max_bags: "" }])} className="text-sm font-semibold text-emerald-800">+ Add bag type limit</button>
            </div>
          )}
        </div>
        <button onClick={save} disabled={saving} data-testid={`save-${entity}-btn`}
          className="mt-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {record ? "Update" : "Save"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function UsersPanel() {
  const { roles } = useAuth();
  const qc = useQueryClient();
  const { data = [], isLoading, isError } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/auth/users")).data, retry: false });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const remove = async (row) => {
    if (!window.confirm(`Deactivate user ${row.email}?`)) return;
    try {
      await api.delete(`/auth/users/${row.id}`);
      toast.success("User deactivated");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const restore = async (row) => {
    try {
      await api.put(`/auth/users/${row.id}`, { active: true });
      toast.success("User restored");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid="add-user-btn" onClick={() => { setEditing(null); setOpen(true); }}
          className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
          <Plus size={16} /> Add User
        </button>
      </div>
      {isError ? (
        <Card className="p-6 text-sm text-stone-500">Only Super Admin / Management can manage users.</Card>
      ) : isLoading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
              <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} className="border-t border-stone-100 even:bg-stone-50/50">
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">{u.role}</td>
                  <td className="px-4 py-3"><StatusBadge tone={u.active === false ? "neutral" : "emerald"}>{u.active === false ? "Inactive" : "Active"}</StatusBadge></td>
                  <td className="px-4 py-3">
                    <CrudActions
                      testid={`user-${u.id}`}
                      inactive={u.active === false}
                      onEdit={() => { setEditing(u); setOpen(true); }}
                      onDelete={() => remove(u)}
                      onRestore={u.active === false ? () => restore(u) : undefined}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <UserDialog open={open} onClose={() => { setOpen(false); setEditing(null); }} record={editing} roles={roles || []} onSaved={() => qc.invalidateQueries({ queryKey: ["users"] })} />
    </div>
  );
}

function UserDialog({ open, onClose, record, roles, onSaved }) {
  const blank = { name: "", email: "", password: "", role: "Warehouse Operator" };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(record ? { name: record.name, email: record.email, password: "", role: record.role } : blank);
  }, [open, record]);

  const save = async () => {
    if (!f.name || !f.email) return toast.error("Name and email are required");
    if (!record && !f.password) return toast.error("Password is required for a new user");
    setSaving(true);
    try {
      const body = { name: f.name, email: f.email, role: f.role };
      if (f.password) body.password = f.password;
      if (record) await api.put(`/auth/users/${record.id}`, body);
      else await api.post("/auth/users", { ...body, password: f.password });
      toast.success(record ? "User updated" : "User created");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></Field>
          <Field label="Email" required><TextInput type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></Field>
          <Field label={record ? "New password (optional)" : "Password"} required={!record}>
            <TextInput type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" />
          </Field>
          <Field label="Role" required>
            <SelectInput options={(roles.length ? roles : ["Warehouse Operator"]).map((r) => ({ value: r, label: r }))} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
          </Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-user-btn"
          className="mt-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {record ? "Update User" : "Save User"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
